'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { LOCALE_COOKIE } from '@/lib/i18n';

type Lang = 'en' | 'es' | 'pt' | 'de' | 'fr';
export type LegalLang = Lang;

const LANGS: readonly Lang[] = ['en', 'es', 'pt', 'de', 'fr'];
const isLang = (v: unknown): v is Lang => typeof v === 'string' && (LANGS as readonly string[]).includes(v);

/**
 * Idioma de las páginas del grupo (legal): políticas, lista de espera y
 * «Próximamente».
 *   1. Prefijo de la URL (/fr/privacy). Manda siempre y se conoce ya en el
 *      render del servidor, así que no hay destello en inglés y el cambio
 *      con el selector se ve al instante.
 *   2. Sin prefijo: cookie ezdocu-locale → localStorage['lang'] (legado) →
 *      idioma del navegador → en.
 */
export function useLegalLocale(): Lang {
  const pathname = usePathname() || '/';
  const seg = pathname.split('/', 2)[1];
  const fromUrl = isLang(seg) ? seg : null;
  const [fallback, setFallback] = useState<Lang>('en');

  useEffect(() => {
    if (fromUrl) return;
    try {
      const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=([^;]+)`));
      if (m && isLang(m[1])) { setFallback(m[1]); return; }
      const saved = localStorage.getItem('lang');
      if (isLang(saved)) { setFallback(saved); return; }
    } catch {
      // Cookies o storage bloqueados: se sigue con el navegador.
    }
    const browserLang = navigator.language?.split('-')[0];
    if (isLang(browserLang)) setFallback(browserLang);
  }, [fromUrl]);

  return fromUrl ?? fallback;
}

const policyTitles: Record<string, Record<Lang, string>> = {
  'Privacy Policy': {
    en: 'Privacy Policy',
    es: 'Política de privacidad',
    pt: 'Política de privacidade',
    de: 'Datenschutzrichtlinie',
    fr: 'Politique de confidentialité',
  },
  'Terms of Service': {
    en: 'Terms of Service',
    es: 'Términos de servicio',
    pt: 'Termos de serviço',
    de: 'Nutzungsbedingungen',
    fr: 'Conditions d\'utilisation',
  },
  'Cookie Policy': {
    en: 'Cookie Policy',
    es: 'Política de cookies',
    pt: 'Política de cookies',
    de: 'Cookie-Richtlinie',
    fr: 'Politique en matière de cookies',
  },
  'Disclaimer': {
    en: 'Disclaimer',
    es: 'Descargo de responsabilidad',
    pt: 'Aviso Legal',
    de: 'Haftungsausschluss',
    fr: 'Avertissement',
  },
};

export function TermlyPolicy({ policyId, title }: { policyId: string; title: string }) {
  const lang = useLegalLocale();

  const translatedTitle = policyTitles[title]?.[lang] || title;

  // Sin `lang`, Termly elige por el idioma del navegador: /es/terms salía en
  // alemán con un navegador en alemán.
  return (
    <div className="w-full h-[calc(100vh-120px)]">
      <iframe
        src={`https://app.termly.io/policy-viewer/policy.html?policyUUID=${policyId}&lang=${lang}`}
        className="w-full h-full border-0"
        title={translatedTitle}
      />
    </div>
  );
}
